import type { UserDto } from "@stylingcv/dto";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type SubscriptionTier = "FREE" | "BASIC" | "PROFESSIONAL" | "ENTERPRISE";

type AuthState = {
  user: UserDto | null;
  cachedSubscriptionTier: SubscriptionTier | null;
  subscriptionTier: SubscriptionTier;
};

type AuthActions = {
  setUser: (user: UserDto | null) => void;
  syncSubscriptionTier: () => void;
};

type AuthGetters = {
  hasBasicAccess: boolean;
  hasProfessionalAccess: boolean;
  hasEnterpriseAccess: boolean;
};

// Helper function to get actual subscription tier from localStorage
const getLocalStorageTier = (): SubscriptionTier | null => {
  try {
    const authData = localStorage.getItem("auth");
    if (!authData) return null;

    const parsed = JSON.parse(authData);
    return parsed.state?.user?.subscriptionTier || null;
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthState & AuthActions & AuthGetters>()(
  persist(
    (set, get) => ({
      user: null,
      cachedSubscriptionTier: null,
      subscriptionTier: "FREE" as SubscriptionTier,

      setUser: (user: UserDto | null) => {
        if (user) {
          const subscriptionTier = user.subscriptionTier || "FREE";
          set({
            user: { ...user, subscriptionTier },
            cachedSubscriptionTier: subscriptionTier,
            subscriptionTier: subscriptionTier,
          });

          // Update localStorage to ensure consistency
          try {
            const currentStorage = localStorage.getItem("auth");
            if (currentStorage) {
              const parsed = JSON.parse(currentStorage);
              if (parsed.state) {
                parsed.state.user = { ...user, subscriptionTier };
                parsed.state.cachedSubscriptionTier = subscriptionTier;
                localStorage.setItem("auth", JSON.stringify(parsed));
              }
            }
          } catch {
            // Silent fail on localStorage error
          }
        } else {
          set({ user: null, cachedSubscriptionTier: null, subscriptionTier: "FREE" });
        }
      },

      syncSubscriptionTier: () => {
        const localStorageTier = getLocalStorageTier();
        const currentUser = get().user;
        const currentTier = get().subscriptionTier;

        if (localStorageTier && currentTier !== localStorageTier) {
          set({
            user: currentUser ? { ...currentUser, subscriptionTier: localStorageTier } : null,
            cachedSubscriptionTier: localStorageTier,
            subscriptionTier: localStorageTier,
          });
        } else if (currentUser?.subscriptionTier && currentTier !== currentUser.subscriptionTier) {
          set({
            cachedSubscriptionTier: currentUser.subscriptionTier,
            subscriptionTier: currentUser.subscriptionTier,
          });
        }
      },

      get hasBasicAccess(): boolean {
        const tier = get().subscriptionTier;
        return tier !== "FREE";
      },
      get hasProfessionalAccess(): boolean {
        const tier = get().subscriptionTier;
        return tier === "PROFESSIONAL" || tier === "ENTERPRISE";
      },
      get hasEnterpriseAccess(): boolean {
        const tier = get().subscriptionTier;
        return tier === "ENTERPRISE";
      },
    }),
    {
      name: "auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user
          ? {
              id: state.user.id,
              name: state.user.name,
              email: state.user.email,
              subscriptionTier: state.user.subscriptionTier,
              freePdfLastExportAt: state.user.freePdfLastExportAt,
              picture: state.user.picture,
              locale: state.user.locale,
              emailVerified: state.user.emailVerified,
            }
          : null,
        cachedSubscriptionTier: state.cachedSubscriptionTier,
        subscriptionTier: state.subscriptionTier,
      }),
      version: 1,
    },
  ),
);
